/**
 * Response time over the last day or week.
 *
 * One series, so no legend box — the title names it, which is the rule. A 2px
 * line, a recessive grid, and a crosshair-plus-tooltip on hover, because an
 * SVG chart in a browser *is* interactive and shipping it inert wastes the
 * medium.
 *
 * ── One axis, and why there is no second one ────────────────────────────────
 * The obvious next feature is plotting check failures on the same chart. It is
 * not here, and will not be: two measures of different scale on two y-axes is
 * the single most misleading thing a chart can do — the crossover point is an
 * artefact of where you put the axes. Failures are already on the bar strip
 * above, at the same dates, which is the honest version of the same comparison.
 *
 * ── Only successful checks are plotted ──────────────────────────────────────
 * The server filters to `ok = 1`. The time a connection took to *fail* is not a
 * response time, and folding it in makes an outage look like a slowdown — the
 * line sags reassuringly instead of the data simply stopping. A gap in this
 * chart means "nothing answered", which is what it should mean.
 */

import { useEffect, useId, useMemo, useState } from 'react';
import { api, type LatencyWire } from '../api';
import { monoPlain, tableHead } from '../theme';
import { ds } from '../ds';

import { useT } from '../i18n';
const H = 132;
const W = 720;
const PAD = { top: 12, right: 8, bottom: 22, left: 42 };

interface Props {
  monitorId: string;
  monitorName: string;
}

/** Nice round gridline values covering 0..max. Never a raw max — an axis
 *  topping out at 431 ms tells you less than one topping out at 500. */
function ticksFor(max: number): number[] {
  if (max <= 0) return [0, 100];
  const rough = max / 3;
  const mag = Math.pow(10, Math.floor(Math.log10(rough)));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= rough) ?? mag * 10;
  const out: number[] = [];
  for (let v = 0; v <= max + step * 0.5; v += step) out.push(Math.round(v));
  return out;
}

export function LatencyChart({ monitorId, monitorName }: Props) {
  const t = useT();
  const { Button } = ds();
  const [hours, setHours] = useState<24 | 168>(24);
  const [data, setData] = useState<LatencyWire | null>(null);
  const [loading, setLoading] = useState(true);
  const [hover, setHover] = useState<number | null>(null);
  const titleId = useId();

  useEffect(() => {
    let live = true;
    setLoading(true);
    api
      .latency(monitorId, hours)
      .then((d) => {
        if (live) setData(d);
      })
      .catch(() => {
        if (live) setData(null);
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [monitorId, hours]);

  const geometry = useMemo(() => {
    const points = data?.points ?? [];
    if (points.length < 2) return null;

    const xs = points.map((p) => p.at);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const maxY = Math.max(...points.map((p) => p.avgMs));
    const ticks = ticksFor(maxY);
    const top = ticks[ticks.length - 1] || 1;

    const x = (at: number) =>
      PAD.left + ((at - minX) / Math.max(1, maxX - minX)) * (W - PAD.left - PAD.right);
    const y = (ms: number) => PAD.top + (1 - ms / top) * (H - PAD.top - PAD.bottom);

    return {
      points,
      ticks,
      x,
      y,
      path: points.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(p.at)},${y(p.avgMs)}`).join(' '),
      average: Math.round(points.reduce((s, p) => s + p.avgMs, 0) / points.length)
    };
  }, [data]);

  const active = hover !== null && geometry ? geometry.points[hover] : null;

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 12,
          marginBottom: 10,
          flexWrap: 'wrap'
        }}
      >
        <div>
          <div style={tableHead}>{t('latency-chart.title')}</div>
          {geometry && (
            <div style={{ ...monoPlain, fontSize: 12 }}>
              {t('latency-chart.average', {
                ms: geometry.average,
                window: t(hours === 24 ? 'latency-chart.window.day' : 'latency-chart.window.week')
              })}
            </div>
          )}
        </div>
        {/* Filters in one row above the chart. */}
        <div style={{ display: 'flex', gap: 6 }}>
          {([24, 168] as const).map((h) => (
            <Button
              key={h}
              size="sm"
              variant={hours === h ? 'soft' : 'ghost'}
              onClick={() => setHours(h)}
            >
              {h === 24 ? '24h' : '7d'}
            </Button>
          ))}
        </div>
      </div>

      {loading && !geometry && (
        <div style={{ ...monoPlain, height: H, display: 'grid', placeItems: 'center' }}>
          {t('latency-chart.loading')}
        </div>
      )}

      {!loading && !geometry && (
        <div
          style={{
            ...monoPlain,
            height: H,
            display: 'grid',
            placeItems: 'center',
            textAlign: 'center'
          }}
        >
          {t('latency-chart.empty')}
        </div>
      )}

      {geometry && (
        <div style={{ position: 'relative' }}>
          <svg
            viewBox={`0 0 ${W} ${H}`}
            width="100%"
            height={H}
            role="img"
            aria-labelledby={titleId}
            style={{ display: 'block', overflow: 'visible' }}
            onMouseLeave={() => setHover(null)}
            onMouseMove={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              const px = ((e.clientX - rect.left) / rect.width) * W;
              // Nearest point rather than the one under the cursor: a crosshair
              // that only snaps when you are exactly on a vertex is a crosshair
              // nobody can use.
              let best = 0;
              let bestD = Infinity;
              geometry.points.forEach((p, i) => {
                const d = Math.abs(geometry.x(p.at) - px);
                if (d < bestD) {
                  bestD = d;
                  best = i;
                }
              });
              setHover(best);
            }}
          >
            {/* The one thing a screen reader gets from an SVG, so it is a
                catalogue string like any other sentence. */}
            <title id={titleId}>
              {t('latency-chart.a11y', {
                name: monitorName,
                ms: geometry.average,
                window: t(hours === 24 ? 'latency-chart.window.day' : 'latency-chart.window.week')
              })}
            </title>

            {/* Recessive grid: hairlines behind the data, labelled at the left. */}
            {geometry.ticks.map((t) => (
              <g key={t}>
                <line
                  x1={PAD.left}
                  x2={W - PAD.right}
                  y1={geometry.y(t)}
                  y2={geometry.y(t)}
                  stroke="var(--border-subtle)"
                  strokeWidth={1}
                />
                <text
                  x={PAD.left - 8}
                  y={geometry.y(t) + 3.5}
                  textAnchor="end"
                  style={{ fontSize: 10, fill: 'var(--text-quiet)' }}
                  fontFamily="var(--font-mono), monospace"
                >
                  {t}
                </text>
              </g>
            ))}

            <text
              x={PAD.left}
              y={H - 4}
              style={{ fontSize: 10, fill: 'var(--text-quiet)' }}
              fontFamily="var(--font-mono), monospace"
            >
              {hours === 24 ? '24 hours ago' : '7 days ago'}
            </text>
            <text
              x={W - PAD.right}
              y={H - 4}
              textAnchor="end"
              style={{ fontSize: 10, fill: 'var(--text-quiet)' }}
              fontFamily="var(--font-mono), monospace"
            >
              {t('latency-chart.now')}
            </text>

            {active && (
              <line
                x1={geometry.x(active.at)}
                x2={geometry.x(active.at)}
                y1={PAD.top}
                y2={H - PAD.bottom}
                stroke="var(--border-strong)"
                strokeWidth={1}
              />
            )}

            <path
              d={geometry.path}
              fill="none"
              stroke="var(--st-up-ink)"
              strokeWidth={2}
              strokeLinejoin="round"
              strokeLinecap="round"
            />

            {active && (
              <circle
                cx={geometry.x(active.at)}
                cy={geometry.y(active.avgMs)}
                r={4.5}
                fill="var(--st-up-ink)"
                // A 2px surface ring, so the marker reads against the line it
                // sits on rather than merging with it.
                stroke="var(--surface-card)"
                strokeWidth={2}
              />
            )}
          </svg>

          {active && (
            <div
              role="tooltip"
              style={{
                position: 'absolute',
                top: -6,
                left: `${Math.min(86, Math.max(14, (geometry.x(active.at) / W) * 100))}%`,
                transform: 'translateX(-50%)',
                background: 'var(--surface-raised)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 10,
                boxShadow: '0 18px 44px rgba(40,28,45,.18)',
                padding: '8px 11px',
                pointerEvents: 'none',
                zIndex: 5,
                whiteSpace: 'nowrap'
              }}
            >
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-strong)' }}>
                {t('latency-chart.tip.average', { ms: active.avgMs })}
              </div>
              <div style={{ ...monoPlain, fontSize: 11 }}>
                {new Date(active.at * 1000).toLocaleString(undefined, {
                  day: 'numeric',
                  month: 'short',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </div>
              <div style={{ ...monoPlain, fontSize: 11 }}>
                {t('latency-chart.tip.peak', { ms: active.maxMs, checks: active.checks })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
